<template>
    <div v-if="lastChangeInfo" class="last-changed-info">
        <div class="last-change-header">Last changed by:</div>
        <div class="last-change-content">
            <Avatar
                :image="userFromStore?.profile_picture ?? undefined"
                :label="
                    userFromStore?.profile_picture
                        ? undefined
                        : getInitials(lastChangeInfo.user_name)
                "
                shape="circle"
                size="normal"
                class="user-avatar"
            />
            <div class="details">
                <span class="username">{{ lastChangeInfo.user_name }}</span>
                <span class="timestamp">{{ formattedDate }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import Avatar from "primevue/avatar";
import { computed, ref, watch } from "vue";

import { useServices } from "@/lib/openapi";
import { HistoryEntry } from "@/lib/openapi/gen/types.gen";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { useUsersManager, useUsersStore } from "@/stores/users-store";

const { isFetching } = useCurrentEntityManager();

const props = defineProps<{
    entityId: string;
    entityType: EntityType;
    attribute?: string;
}>();

interface LastChangeInfo {
    user_name: string;
    photo_url?: string | null;
    timestamp: number;
}

// Module-level cache to track which entities don't have history to avoid repeated 404s
// This persists across component instances
const entitiesWithoutHistory = new Set<string>();

const lastChangeInfo = ref<LastChangeInfo | null>(null);
const { users } = useUsersManager();
const usersStore = useUsersStore();

const api = useServices({
    requires: {
        auth: true,
        organization: true,
        stek: false,
    },
});

// Find the user in the users store by name
const userFromStore = computed(() => {
    if (!lastChangeInfo.value) return null;
    return users.value.find(
        (user) => user.name === lastChangeInfo.value!.user_name
    );
});

const formattedDate = computed(() => {
    if (!lastChangeInfo.value) return "";

    const date = new Date(lastChangeInfo.value.timestamp);

    // If it's today, show time only
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
        return `at ${date.toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
        })}`;
    }

    // If it's this year, show day and month
    if (date.getFullYear() === today.getFullYear()) {
        return `on ${date.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
        })}`;
    }

    // Otherwise show full date
    return `on ${date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    })}`;
});

const getInitials = (name: string) => {
    return name
        .split(" ")
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase()
        .substring(0, 2);
};

const fetchLastChangeInfo = async () => {
    if (!props.entityId || !props.entityType) return;

    // Check cache - if we know this entity doesn't have history, don't make the request
    // This prevents repeated 404 errors in the console
    const attribute = props.attribute || "description";
    const cacheKey = `${props.entityType}:${props.entityId}:${attribute}`;
    if (entitiesWithoutHistory.has(cacheKey)) {
        return;
    }

    // Load users to ensure we have profile pictures available
    await usersStore.getAllUsers();

    try {
        const { data, response } =
            await api.startBizEntitiesEntityTypeEntityIdAttributeHistoryGet({
                throwOnError: false, // Don't throw on 404 - it's expected for entities without history
                path: {
                    entity_type: props.entityType,
                    entity_id: props.entityId,
                    attribute,
                },
                query: {
                    limit: 1, // Get only the most recent change
                },
            });

        // Silently handle 404 errors - it's normal for entities without history
        if (response.status === 404) {
            // Cache this entity as not having history to avoid future requests
            entitiesWithoutHistory.add(cacheKey);
            return;
        }

        if (response.ok && data) {
            // Extract the most recent history entry
            const historyEntries = data.history as HistoryEntry[];

            if (historyEntries && historyEntries.length > 0) {
                const latestEntry = historyEntries[0];

                lastChangeInfo.value = {
                    user_name: latestEntry.author.name,
                    photo_url: null, // API doesn't provide this
                    timestamp: new Date(latestEntry.author.date).getTime(),
                };
            }
        }
    } catch (error: unknown) {
        // Silently handle 404 errors - it's normal for entities without history
        // Check if error has response with 404 status
        const errorObj = error as {
            response?: { status?: number; url?: string };
            detail?: unknown[];
            status?: number;
            statusText?: string;
        };

        // Check multiple ways the error might indicate a 404
        const is404 =
            errorObj?.response?.status === 404 ||
            errorObj?.status === 404 ||
            (errorObj?.response?.url?.includes("/biz/entities/") &&
                errorObj?.response?.url?.includes("/history") &&
                errorObj?.response?.status === 404);

        // Also check if the error detail indicates NOT_FOUND
        const isNotFound =
            errorObj?.detail &&
            Array.isArray(errorObj.detail) &&
            errorObj.detail.length > 0 &&
            typeof errorObj.detail[0] === "object" &&
            errorObj.detail[0] !== null &&
            "type" in errorObj.detail[0] &&
            errorObj.detail[0].type === "NOT_FOUND";

        if (is404 || isNotFound) {
            // Entity or attribute doesn't have history yet - this is expected
            // Cache this entity to avoid future requests
            const attribute = props.attribute || "description";
            const cacheKey = `${props.entityType}:${props.entityId}:${attribute}`;
            entitiesWithoutHistory.add(cacheKey);
            // Silently return without logging
            return;
        }

        // Only log unexpected errors (not 404s)
        if (errorObj?.response?.status !== 404 && errorObj?.status !== 404) {
            console.error("Error fetching entity history:", error);
        }
    }
};

watch(
    isFetching,
    async () => {
        if (!isFetching.value) {
            await fetchLastChangeInfo();
        }
    },
    { immediate: true }
);
</script>

<style scoped lang="scss">
.last-changed-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    white-space: nowrap;
    padding: 0.25rem 0.5rem;
}

.last-change-header {
    font-size: 0.7rem;
    color: var(--text-color) !important;
    margin-bottom: 0.2rem;
}

.last-change-content {
    display: flex;
    align-items: center;
}

.user-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    margin-right: 0.5rem;
    object-fit: cover;
}

.details {
    display: flex;
    flex-direction: column;
}

.username {
    font-weight: 500;
    font-size: 0.7rem;
}

.timestamp {
    font-size: 0.6rem;
    color: var(--text-color-secondary);
}
</style>
